/**
 * Generate distinct colors for clusters
 * @param {Number} count - Number of colors needed
 * @returns {Array} Array of hex color strings
 */
export const generateClusterColors = (count) => {
	const colors = [
		"#FF6384",
		"#36A2EB",
		"#FFCE56",
		"#4BC0C0",
		"#9966FF",
		"#FF9F40",
		"#FF6384",
		"#C9CBCF",
		"#4BC0C0",
		"#FF6384",
		"#36A2EB",
		"#FFCE56",
	];

	if (count <= colors.length) {
		return colors.slice(0, count);
	}

	// Generate additional colors if needed
	const additionalColors = [];
	for (let i = colors.length; i < count; i++) {
		const hue = (i * 137.508) % 360; // Golden angle approximation
		additionalColors.push(hslToHex(hue, 70, 60));
	}

	return [...colors, ...additionalColors];
};

/**
 * Convert HSL color to Hex
 * @param {Number} h - Hue (0-360)
 * @param {Number} s - Saturation (0-100)
 * @param {Number} l - Lightness (0-100)
 * @returns {String} Hex color string
 */
const hslToHex = (h, s, l) => {
	s /= 100;
	l /= 100;

	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;

	let r = 0,
		g = 0,
		b = 0;

	if (0 <= h && h < 60) {
		r = c;
		g = x;
	} else if (60 <= h && h < 120) {
		r = x;
		g = c;
	} else if (120 <= h && h < 180) {
		g = c;
		b = x;
	} else if (180 <= h && h < 240) {
		g = x;
		b = c;
	} else if (240 <= h && h < 300) {
		r = x;
		b = c;
	} else if (300 <= h && h < 360) {
		r = c;
		b = x;
	}

	const toHex = (val) => {
		const hex = Math.round((val + m) * 255).toString(16);
		return hex.length === 1 ? "0" + hex : hex;
	};

	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Convert hex color to RGB array
 * @param {String} hex - Hex color string
 * @returns {Array} RGB array [r, g, b]
 */
export const hexToRgb = (hex) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
};

/**
 * Calculate center point of a cluster
 * @param {Array} points - Array of points with cx, cy coordinates
 * @returns {Object} Center point {lat, lng}
 */
export const calculateClusterCenter = (points) => {
	if (!points || points.length === 0) {
		return {lat: 0, lng: 0};
	}

	const sum = points.reduce(
		(acc, point) => {
			acc.lat += parseFloat(point.cy || point.latitude || 0);
			acc.lng += parseFloat(point.cx || point.longitude || 0);
			return acc;
		},
		{lat: 0, lng: 0}
	);

	return {
		lat: sum.lat / points.length,
		lng: sum.lng / points.length,
	};
};

/**
 * Calculate bounds for a set of points
 * @param {Array} points - Array of points
 * @returns {Object} Bounds {north, south, east, west}
 */
export const calculateBounds = (points) => {
	if (!points || points.length === 0) {
		return null;
	}

	const lats = points.map((p) => parseFloat(p.cy || p.latitude || 0));
	const lngs = points.map((p) => parseFloat(p.cx || p.longitude || 0));

	return {
		north: Math.max(...lats),
		south: Math.min(...lats),
		east: Math.max(...lngs),
		west: Math.min(...lngs),
	};
};

/**
 * Format point for deck.gl layer
 * @param {Object} point - Point data
 * @param {String} color - Hex color (optional, uses point.cluster_color if available)
 * @param {Number} clusterId - Cluster ID (optional, uses point.cluster_identification if available)
 * @returns {Object} Formatted point
 */
export const formatPointForDeckGL = (point, color = null, clusterId = null) => {
	const finalColor = color || point.cluster_color || "#4285F4";
	const finalClusterId = clusterId !== null ? clusterId : point.cluster_identification;

	return {
		position: [parseFloat(point.cx || point.longitude || 0), parseFloat(point.cy || point.latitude || 0)],
		color: hexToRgb(finalColor),
		radius: 8,
		clusterId: finalClusterId,
		microClusterId: point.micro_cluster_id,
		data: point,
	};
};

/**
 * Validate clustering form data
 * @param {Object} data - Form data
 * @returns {Object} {valid: Boolean, errors: Array}
 */
export const validateClusteringForm = (data) => {
	const errors = [];

	if (!data.name || data.name.trim().length === 0) {
		errors.push("clustering.validation.name_required");
	}

	if (!data.total_clusters || data.total_clusters < 1) {
		errors.push("clustering.validation.total_clusters_required");
	}

	if (data.total_clusters > 10) {
		errors.push("clustering.validation.total_clusters_max");
	}

	if (!data.points || data.points.length === 0) {
		errors.push("clustering.validation.points_required");
	}

	if (data.points && data.points.length > 500) {
		errors.push("clustering.validation.points_max");
	}

	return {
		valid: errors.length === 0,
		errors,
	};
};

/**
 * Group points by cluster ID
 * @param {Array} points - Array of points with cluster_identification
 * @returns {Object} Object with cluster_identification as key and points array as value
 */
export const groupPointsByCluster = (points) => {
	return points.reduce((acc, point) => {
		const clusterId = point.cluster_identification !== undefined ? point.cluster_identification : "unassigned";
		if (!acc[clusterId]) {
			acc[clusterId] = [];
		}
		acc[clusterId].push(point);
		return acc;
	}, {});
};

/**
 * Group points by micro cluster ID
 * @param {Array} points - Array of points with micro_cluster_id
 * @returns {Object} Object with micro_cluster_id as key and points array as value
 */
export const groupPointsByMicroCluster = (points) => {
	return points.reduce((acc, point) => {
		const microClusterId = point.micro_cluster_id || "unassigned";
		if (!acc[microClusterId]) {
			acc[microClusterId] = [];
		}
		acc[microClusterId].push(point);
		return acc;
	}, {});
};

/**
 * Get unique colors from clustered points
 * @param {Array} points - Array of points with cluster_color
 * @returns {Array} Array of unique colors
 */
export const getUniqueColorsFromPoints = (points) => {
	const colorsSet = new Set();
	points.forEach((point) => {
		if (point.cluster_color) {
			colorsSet.add(point.cluster_color);
		}
	});
	return Array.from(colorsSet);
};

/**
 * Calculate statistics for a cluster
 * @param {Array} points - Array of points in cluster
 * @returns {Object} Statistics {totalPoints, center, bounds}
 */
export const calculateClusterStats = (points) => {
	const totalDemand = points.reduce((sum, point) => {
		return sum + (parseInt(point.demand) || 0);
	}, 0);

	return {
		totalPoints: points.length,
		totalDemand: totalDemand,
		center: calculateClusterCenter(points),
		bounds: calculateBounds(points),
	};
};

/**
 * Calculate Haversine distance between two lat/lng points in kilometers
 * @param {Object} point1 - {latitude, longitude}
 * @param {Object} point2 - {latitude, longitude}
 * @returns {Number} Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
	const R = 6371; // Earth's radius in km
	const lat1 = parseFloat(point1.latitude || point1.cy);
	const lon1 = parseFloat(point1.longitude || point1.cx);
	const lat2 = parseFloat(point2.latitude || point2.cy);
	const lon2 = parseFloat(point2.longitude || point2.cx);

	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const distance = R * c;

	return distance;
};

/**
 * Calculate compactness metrics for a cluster
 * @param {Array} points - Array of points in the cluster
 * @returns {Object} Compactness metrics {avgRadiusFromCenter, maxDistanceBetweenPoints, convexHullArea, compactnessRatio}
 */
export const calculateCompactnessMetrics = (points) => {
	if (!points || points.length === 0) {
		return {
			avgRadiusFromCenter: 0,
			maxDistanceBetweenPoints: 0,
			convexHullArea: 0,
			compactnessRatio: 0,
		};
	}

	// Calculate centroid
	const center = calculateClusterCenter(points);

	// Calculate average radius from center
	const distancesFromCenter = points.map((point) => calculateDistance(center, point));
	const avgRadiusFromCenter = distancesFromCenter.reduce((sum, d) => sum + d, 0) / points.length;

	// Calculate maximum distance between any two points
	let maxDistance = 0;
	for (let i = 0; i < points.length; i++) {
		for (let j = i + 1; j < points.length; j++) {
			const dist = calculateDistance(points[i], points[j]);
			if (dist > maxDistance) {
				maxDistance = dist;
			}
		}
	}

	// Calculate convex hull area (approximate using bounding box for simplicity)
	if (points.length < 3) {
		return {
			avgRadiusFromCenter: parseFloat(avgRadiusFromCenter.toFixed(3)),
			maxDistanceBetweenPoints: parseFloat(maxDistance.toFixed(3)),
			convexHullArea: 0,
			compactnessRatio: 0,
		};
	}

	// Get bounding box
	const lats = points.map((p) => parseFloat(p.latitude || p.cy));
	const lngs = points.map((p) => parseFloat(p.longitude || p.cx));
	const minLat = Math.min(...lats);
	const maxLat = Math.max(...lats);
	const minLng = Math.min(...lngs);
	const maxLng = Math.max(...lngs);

	// Approximate area using bounding box (in km²)
	const latDist = calculateDistance({latitude: minLat, longitude: minLng}, {latitude: maxLat, longitude: minLng});
	const lngDist = calculateDistance({latitude: minLat, longitude: minLng}, {latitude: minLat, longitude: maxLng});
	const area = latDist * lngDist;

	// Calculate perimeter (approximate)
	const perimeter = 2 * (latDist + lngDist);

	// Compactness ratio (closer to 1 = more compact)
	// For a circle: 4π*A / P² = 1
	// For other shapes: < 1
	const compactnessRatio = perimeter > 0 ? (4 * Math.PI * area) / (perimeter * perimeter) : 0;

	return {
		avgRadiusFromCenter: parseFloat(avgRadiusFromCenter.toFixed(3)),
		maxDistanceBetweenPoints: parseFloat(maxDistance.toFixed(3)),
		convexHullArea: parseFloat(area.toFixed(3)),
		compactnessRatio: parseFloat(compactnessRatio.toFixed(3)),
	};
};
